import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { createTask } from '@/entities/task/api/tasks';
import { invalidateTaskCaches } from '@/features/tasks/model/useTaskActions';
import { fetchUsers } from '@/lib/api/users';
import { http } from '@/shared/api/http';
import { useAuthStore } from '@/store/auth';
import { useTaskFiltersStore } from '@/entities/task/model/stores/task-filters';
import { SelectBlock } from './SelectBlock';

import { fetchDeals, fetchDealFunnels } from '@/entities/task/api/сrm';
import {
  fetchProjects,
  fetchSmartProcessFunnels,
} from '@/entities/task/api/projects';
import {
  buildMeetingFunnelOptionGroups,
  buildMeetingFunnelOptions,
  findFunnelOption,
  getFunnelSelectionKey,
  normalizeFunnelLabel,
} from '../model/funnels';
import { DEFAULT_FUNNEL_LABEL, type FunnelKind } from '../model/types';

type SlotDuration = 30 | 60;

const MEETING_GROUP_ID = 6;

function getMaybeString(v: ANY): string {
  if (!v) {
    return '';
  }
  if (typeof v === 'string') {
    return v;
  }
  const valid = v.Valid ?? v.valid;
  const str = v.String ?? v.string;
  if (typeof str === 'string' && (valid === true || valid === undefined)) {
    return str;
  }
  return '';
}

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ---- CRM Leads ----
async function fetchCrmLeads(search: string): Promise<{ result: ANY[] }> {
  const params = search ? { search } : {};
  const { data } = await http.get<{ result: ANY[] }>('/crm/leads/list', {
    params,
  });
  return data;
}

// ---- Users helpers ----
function userDisplayName(u: ANY): string {
  const first = getMaybeString(u?.name ?? u?.Name);
  const last = getMaybeString(u?.lastName ?? u?.LastName);
  const full = [last, first].filter(Boolean).join(' ').trim();
  return full || first || last || `User#${u?.id ?? u?.ID ?? '??'}`;
}

function userIdValue(u: ANY): number | null {
  const id = u?.id ?? u?.ID;
  return typeof id === 'number' ? id : null;
}

export function SlotCreateModal({
  open,
  slot,
  duration = 60,
  onClose,
}: {
  open: boolean;
  slot: string | null;
  duration?: SlotDuration;
  onClose: () => void;
}) {
  const { userId } = useAuthStore();
  const { selectedDate } = useTaskFiltersStore();
  const queryClient = useQueryClient();

  // хуки ДО return null
  const durationMinutes: SlotDuration = duration === 30 ? 30 : 60;
  const [isSaving, setIsSaving] = useState(false);

  // формат / тип
  const [meetingFormat, setMeetingFormat] = useState<'Онлайн' | 'Оффлайн'>(
    'Онлайн'
  );
  const [meetingType, setMeetingType] = useState<'Продажа' | 'Демо' | 'Аудит'>(
    'Продажа'
  );

  // funnels
  const [funnelLabel, setFunnelLabel] = useState<string>(DEFAULT_FUNNEL_LABEL);

  const { data: funnelsData, isFetching: isSmartFunnelsLoading } = useQuery({
    queryKey: ['smart-process-funnels'],
    queryFn: fetchSmartProcessFunnels,
    enabled: open,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const { data: dealFunnelsData, isFetching: isDealFunnelsLoading } = useQuery({
    queryKey: ['deal-funnels'],
    queryFn: fetchDealFunnels,
    enabled: open,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const funnelOptions = useMemo(
    () =>
      buildMeetingFunnelOptions(
        (funnelsData as ANY)?.result ?? [],
        (dealFunnelsData as ANY)?.result ?? []
      ),
    [dealFunnelsData, funnelsData]
  );

  const isFunnelsLoading = isSmartFunnelsLoading || isDealFunnelsLoading;

  const funnelOptionGroups = useMemo(
    () => buildMeetingFunnelOptionGroups(funnelOptions),
    [funnelOptions]
  );

  const selectedFunnel = useMemo(
    () => findFunnelOption(funnelOptions, funnelLabel),
    [funnelLabel, funnelOptions]
  );

  const funnelKind: FunnelKind = selectedFunnel?.kind ?? 'lead';
  const selectedEntityTypeId = selectedFunnel?.entityTypeId ?? null;
  const selectedDealCategoryId = selectedFunnel?.dealCategoryId ?? null;
  const funnelSelectionKey = selectedFunnel
    ? getFunnelSelectionKey(selectedFunnel)
    : null;

  useEffect(() => {
    const normalizedLabel = normalizeFunnelLabel(funnelLabel);
    const hasOption = funnelOptions.some(
      (funnel) => funnel.label === normalizedLabel
    );

    if (!hasOption) {
      setFunnelLabel(DEFAULT_FUNNEL_LABEL);
      return;
    }

    if (normalizedLabel !== funnelLabel) {
      setFunnelLabel(normalizedLabel);
    }
  }, [funnelLabel, funnelOptions]);

  // -----------------
  // SMART PROJECTS
  // -----------------
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [projectId, setProjectId] = useState<number | null>(null);

  const [projectQuery, setProjectQuery] = useState<string>('');
  const debouncedProjectQuery = useDebouncedValue(projectQuery, 250);

  const { data: projectsData, isFetching: isProjectsLoading } = useQuery({
    queryKey: ['projects-list', selectedEntityTypeId, debouncedProjectQuery],
    queryFn: () =>
      fetchProjects({
        entityTypeId: selectedEntityTypeId ?? undefined,
        search: debouncedProjectQuery.trim() || undefined,
      } as ANY),
    enabled: open && funnelKind === 'smart_process' && !!selectedEntityTypeId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const projectItems = (projectsData as ANY)?.result ?? [];
  const projectByTitle = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const p of projectItems) {
      const t = getMaybeString(p?.title ?? p?.Title) || '';
      if (t && !map.has(t)) {
        map.set(t, p);
      }
    }
    return map;
  }, [projectItems]);

  const projectOptions = useMemo(() => {
    if (funnelKind !== 'smart_process') {
      return [];
    }
    const titles = (Array.isArray(projectItems) ? projectItems : [])
      .map((p: ANY) => getMaybeString(p?.title ?? p?.Title))
      .filter(Boolean);
    return Array.from(new Set(titles));
  }, [funnelKind, projectItems]);

  // -----------------
  // LEADS
  // -----------------
  const [leadLabel, setLeadLabel] = useState<string>('');
  const [leadId, setLeadId] = useState<number | null>(null);
  const [leadCrmRef, setLeadCrmRef] = useState<string | null>(null);

  const [leadQuery, setLeadQuery] = useState<string>('');
  const debouncedLeadQuery = useDebouncedValue(leadQuery, 250);

  const { data: leadsData, isFetching: isLeadsLoading } = useQuery({
    queryKey: ['crm-leads', debouncedLeadQuery],
    queryFn: () => fetchCrmLeads(debouncedLeadQuery.trim()),
    enabled: open && funnelKind === 'lead',
    staleTime: 60 * 1000,
    retry: 1,
  });

  const leadsList = (leadsData as ANY)?.result ?? [];
  const leadByOption = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const l of leadsList) {
      const t = getMaybeString(l?.title ?? l?.Title) || 'Без названия';
      const id = l?.id ?? l?.ID;
      const label = `${t} — #${id}`;
      if (!map.has(label)) {
        map.set(label, l);
      }
    }
    return map;
  }, [leadsList]);

  const leadOptions = useMemo(
    () => Array.from(leadByOption.keys()),
    [leadByOption]
  );

  const leadTitle = useMemo(() => {
    if (!leadLabel) {
      return '';
    }
    return leadLabel.split(' — #')[0]?.trim() || '';
  }, [leadLabel]);

  // -----------------
  // DEALS
  // -----------------
  const [dealLabel, setDealLabel] = useState<string>('');
  const [dealId, setDealId] = useState<number | null>(null);
  const [dealQuery, setDealQuery] = useState<string>('');
  const debouncedDealQuery = useDebouncedValue(dealQuery, 250);

  const { data: dealsData, isFetching: isDealsLoading } = useQuery({
    queryKey: ['meeting-deals', selectedDealCategoryId, debouncedDealQuery],
    queryFn: () =>
      fetchDeals({
        page: 1,
        limit: 100,
        CATEGORY_ID: selectedDealCategoryId ?? undefined,
        title: debouncedDealQuery.trim() || undefined,
      }),
    enabled: open && funnelKind === 'deal' && selectedDealCategoryId !== null,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const dealsList = (dealsData as ANY)?.result ?? [];
  const dealByOption = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const deal of dealsList) {
      const title =
        getMaybeString(deal?.title ?? deal?.Title) || 'Без названия';
      const id = deal?.id ?? deal?.ID;
      const label = `${title} — #${id}`;
      if (!map.has(label)) {
        map.set(label, deal);
      }
    }
    return map;
  }, [dealsList]);

  const dealOptions = useMemo(
    () => Array.from(dealByOption.keys()),
    [dealByOption]
  );

  const dealTitle = useMemo(() => {
    if (!dealLabel) {
      return '';
    }
    return dealLabel.split(' — #')[0]?.trim() || '';
  }, [dealLabel]);

  // -----------------
  // USERS (executor) via /users?search=
  // -----------------
  const [presenterName, setPresenterName] = useState<string>('');
  const [presenterId, setPresenterId] = useState<number | null>(null);
  const [presenterOpened, setPresenterOpened] = useState(false);

  const [presenterQuery, setPresenterQuery] = useState<string>('');
  const debouncedPresenterQuery = useDebouncedValue(presenterQuery, 250);

  const { data: usersData, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users', debouncedPresenterQuery],
    queryFn: () => fetchUsers(debouncedPresenterQuery.trim()),
    enabled: presenterOpened,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const usersList = (usersData as ANY)?.result ?? [];
  const userByLabel = useMemo(() => {
    const map = new Map<string, ANY>();
    for (const u of usersList) {
      const label = userDisplayName(u);
      if (label && !map.has(label)) {
        map.set(label, u);
      }
    }
    return map;
  }, [usersList]);

  const presenterOptions = useMemo(() => {
    const base = ['Я'];
    const extra = (Array.isArray(usersList) ? usersList : [])
      .map((u: ANY) => userDisplayName(u))
      .filter(Boolean);

    const uniq = Array.from(new Set(extra)).filter((x) => x !== 'Я');
    return [...base, ...uniq];
  }, [usersList]);

  useEffect(() => {
    if (open) {
      setPresenterOpened(true);
      setPresenterQuery('');
    }
  }, [open]);

  // -----------------
  // reset on funnel change
  // -----------------
  useEffect(() => {
    setProjectTitle('');
    setProjectId(null);
    setProjectQuery('');

    setLeadLabel('');
    setLeadId(null);
    setLeadCrmRef(null);
    setLeadQuery('');

    setDealLabel('');
    setDealId(null);
    setDealQuery('');
  }, [funnelSelectionKey]);

  // -----------------
  // title/deadline
  // -----------------
  const crmEntityName =
    funnelKind === 'lead'
      ? leadTitle || '—'
      : funnelKind === 'deal'
        ? dealTitle || '—'
        : projectTitle || '—';

  const title = useMemo(() => {
    return `Встреча / ${meetingFormat} / ${meetingType} / ${crmEntityName}`;
  }, [meetingFormat, meetingType, crmEntityName]);

  const deadlineISO = useMemo(() => {
    if (!slot) {
      return null;
    }

    // парсим начало слота (например: "9:00" из "9:00 - 10:00")
    const parts = slot.split('-').map((s) => s.trim());
    const startPart = parts[0]; // начало слота (например "9:00")
    if (!startPart) {
      return null;
    }

    const [hhStr, mmStr] = startPart.split(':');
    const hh = Number(hhStr);
    const mm = Number(mmStr);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
      return null;
    }

    const d = new Date(selectedDate);
    d.setHours(hh, mm, 0, 0); // ставим дедлайн на начало слота (например: 9:00)

    // Отнимаем 5 часов, чтобы компенсировать таймзону Битрикса
    d.setHours(d.getHours() - 5);

    console.log(d);

    return format(d, "yyyy-MM-dd'T'HH:mm:ss'Z'");
  }, [slot, selectedDate]);

  const resolveResponsibleId = async (): Promise<number | null> => {
    if (!userId) {
      return null;
    }
    if (presenterName === 'Я') {
      return userId;
    }
    if (presenterId) {
      return presenterId;
    }

    // fallback
    try {
      const res = await fetchUsers(presenterName);
      const found = res?.result?.[0];
      return userIdValue(found);
    } catch {
      return null;
    }
  };

  const toHexTypeId = (n: number) => n.toString(16).toLowerCase();

  const buildUfCrmTask = (): string[] => {
    if (funnelKind === 'lead') {
      if (!leadId) {
        return [];
      }
      return [leadCrmRef ?? `L_${leadId}`];
    }

    if (funnelKind === 'deal') {
      if (!dealId) {
        return [];
      }
      return [`D_${dealId}`];
    }

    if (selectedEntityTypeId && projectId) {
      return [`T${toHexTypeId(selectedEntityTypeId)}_${projectId}`];
    }

    return [];
  };

  const handleSave = async () => {
    if (!slot) {
      return toast.error('Слот не выбран');
    }
    if (!deadlineISO) {
      return toast.error('Не смог разобрать время слота');
    }
    if (!userId) {
      return toast.error('Не определён пользователь');
    }

    if (funnelKind === 'lead') {
      if (!leadId) {
        return toast.error('Выбери лид из списка');
      }
    } else if (funnelKind === 'deal') {
      if (!dealId) {
        return toast.error('Выбери сделку из списка');
      }
    } else if (!projectId) {
      return toast.error('Выбери проект из списка');
    }

    const UF_CRM_TASK = buildUfCrmTask();
    if (UF_CRM_TASK.length === 0) {
      return toast.error('CRM-привязка не сформирована');
    }

    setIsSaving(true);
    try {
      const responsibleId = await resolveResponsibleId();
      if (!responsibleId) {
        return toast.error('Не удалось определить исполнителя');
      }

      const payload = {
        TITLE: title,
        DESCRIPTION: [
          `Слот: ${slot}`,
          `Формат: ${meetingFormat}`,
          `Тип: ${meetingType}`,
          `CRM: ${UF_CRM_TASK.join(', ')}`,
          `Исполнитель: ${presenterName}`,
        ].join('\n'),

        RESPONSIBLE_ID: responsibleId,
        DEADLINE: deadlineISO,
        TIME_ESTIMATE: durationMinutes * 60,
        GROUP_ID: MEETING_GROUP_ID,

        isMeeting: true,
        UF_CRM_TASK,
      } as ANY;

      console.log(payload);

      await createTask(payload);
      invalidateTaskCaches(queryClient);
      toast.success('Встреча создана');
      onClose();
    } catch (e) {
      console.error('create meeting error:', e);
      toast.error('Не удалось создать задачу');
    } finally {
      setIsSaving(false);
    }
  };
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overscroll-contain">
      <div
        className="absolute inset-0 bg-black/40 animate-in fade-in-0 duration-300"
        onClick={onClose}
      />

      <div className="relative rounded-[20px] p-[24px] max-h-[90dvh] max-w-2xl overflow-hidden  flex flex-col animate-in fade-in-0 zoom-in-95 duration-300 ease-out">
        <div
          className="
          bg-white
          rounded-[10px]
          h-full
          min-h-0
              flex
              flex-col
              
              font-roboto
            "
        >
          <div className="px-3 pt-6">
            <div className="relative rounded-xl bg-card-gradient text-white py-[12px] px-[10px] text-center font-semibold">
              {slot}

              <button
                onClick={onClose}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-[12px] pt-[14px] space-y-[12px]">
            {/* формат */}
            <div className="flex gap-3 justify-center">
              <Chip
                label="Онлайн"
                selected={meetingFormat === 'Онлайн'}
                onClick={() => setMeetingFormat('Онлайн')}
              />
              <Chip
                label="Оффлайн"
                selected={meetingFormat === 'Оффлайн'}
                onClick={() => setMeetingFormat('Оффлайн')}
              />
            </div>

            <Divider />

            {/* тип */}
            <div className="flex gap-3 justify-center flex-wrap">
              <Chip
                label="Продажа"
                selected={meetingType === 'Продажа'}
                onClick={() => setMeetingType('Продажа')}
              />
              <Chip
                label="Демо"
                selected={meetingType === 'Демо'}
                onClick={() => setMeetingType('Демо')}
              />
              <Chip
                label="Аудит"
                selected={meetingType === 'Аудит'}
                onClick={() => setMeetingType('Аудит')}
              />
            </div>

            <Divider />

            {/* Воронка: picker */}
            <SelectBlock
              title="Воронка"
              variant="picker"
              optionGroups={funnelOptionGroups}
              value={funnelLabel}
              onChange={setFunnelLabel}
              allowCustom={false}
              isLoading={isFunnelsLoading}
            />

            <Divider />

            {/* CRM сущность */}
            {funnelKind === 'lead' ? (
              <SelectBlock
                title="Лид"
                variant="search"
                options={leadOptions}
                value={leadLabel}
                onChange={(v) => {
                  setLeadLabel(v);

                  const lead = leadByOption.get(v);
                  const id = lead?.id ?? lead?.ID;
                  setLeadId(typeof id === 'number' ? id : null);

                  const ref = lead?.crmRef;
                  setLeadCrmRef(typeof ref === 'string' ? ref : null);

                  // инпут поиска ведём по названию (без "#id")
                  const t = v.split(' — #')[0]?.trim() || '';
                  setLeadQuery(t);
                }}
                searchQuery={leadQuery}
                onSearchQueryChange={(q) => {
                  setLeadQuery(q);
                  // пока печатает — сброс выбора
                  setPresenterOpened(true);
                  setLeadLabel('');
                  setLeadId(null);
                  setLeadCrmRef(null);
                }}
                placeholder="Найти лид…"
                allowCustom={false}
                isLoading={isLeadsLoading}
              />
            ) : null}

            {funnelKind === 'smart_process' ? (
              <SelectBlock
                title="Проект"
                variant="search"
                options={projectOptions}
                value={projectTitle}
                onChange={(v) => {
                  setProjectTitle(v);
                  setProjectQuery(v);

                  const found = projectByTitle.get(v);
                  const id = (found?.id ?? found?.ID) as number | undefined;
                  setProjectId(typeof id === 'number' ? id : null);
                }}
                searchQuery={projectQuery}
                onSearchQueryChange={(q) => {
                  setProjectQuery(q);
                  setProjectTitle(q);
                  const found = projectByTitle.get(q);
                  const id = (found?.id ?? found?.ID) as number | undefined;
                  setProjectId(typeof id === 'number' ? id : null);
                }}
                placeholder="Найти проект…"
                allowCustom={false}
                isLoading={isProjectsLoading}
              />
            ) : null}

            {funnelKind === 'deal' ? (
              <SelectBlock
                title="Сделка"
                variant="search"
                options={dealOptions}
                value={dealLabel}
                onChange={(v) => {
                  setDealLabel(v);

                  const deal = dealByOption.get(v);
                  const id = deal?.id ?? deal?.ID;
                  setDealId(typeof id === 'number' ? id : null);

                  const title = v.split(' — #')[0]?.trim() || '';
                  setDealQuery(title);
                }}
                searchQuery={dealQuery}
                onSearchQueryChange={(q) => {
                  setDealQuery(q);
                  setDealLabel('');
                  setDealId(null);
                }}
                placeholder="Найти сделку…"
                allowCustom={false}
                isLoading={isDealsLoading}
              />
            ) : null}

            <Divider />

            {/* Исполнитель: search + /users */}
            <SelectBlock
              title="Кто проведёт?"
              variant="search"
              options={presenterOptions}
              value={presenterName}
              onChange={(v) => {
                setPresenterName(v);
                setPresenterQuery(v);

                if (v === 'Я') {
                  setPresenterId(null);
                  return;
                }

                const u = userByLabel.get(v);
                setPresenterId(userIdValue(u));
              }}
              searchQuery={presenterQuery}
              onSearchQueryChange={(q) => {
                setPresenterOpened(true);
                setPresenterQuery(q);
                setPresenterName(q);

                // если совпало — поставим ID, иначе null
                const u = userByLabel.get(q);
                setPresenterId(userIdValue(u));
              }}
              placeholder="Найти сотрудника…"
              allowCustom={false}
              isLoading={isUsersLoading}
            />

            <Divider />

            <div className="px-[13px] pt-1">
              <p className="text-[12px] text-white/70 leading-[130%]">
                Название задачи:
              </p>
              <p className="text-[12px] font-semibold leading-[130%]">
                {title}
              </p>
            </div>
          </div>

          <div className="mx-auto border border-black py-[4px] rounded-2xl my-4">
            <button
              onClick={handleSave}
              disabled={isSaving || !slot}
              className="
              
                py-[6px] px-[19px]
                text-[12px] font-medium 
                hover:bg-white/10 transition
                disabled:opacity-50 disabled:hover:bg-transparent
              "
            >
              {isSaving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Chip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        px-[20px] py-[6px]
        rounded-[10px]
        border
        text-[12px] tracking-[-0.5px]
        transition
        ${selected ? 'bg-[#3B4D7F] text-white' : ''}
      `}
    >
      {label}
    </button>
  );
}

function Divider() {
  return <div className="h-px bg-white/60" />;
}
